import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ShieldAlert,
  ShieldX,
  Users,
  FileText,
  Download,
  Plus,
  X,
  Circle,
  Trash2,
  Activity,
  ScrollText,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

// ─── Severity → token map ──────────────────────────────────────────
const SEVERITY = {
  high:   { fg: 'var(--danger)', label: 'HIGH' },
  medium: { fg: 'var(--warn)',   label: 'MEDIUM' },
  ok:     { fg: 'var(--ok)',     label: 'OK' },
};

const STATUS = {
  blocked: { fg: 'var(--danger)', bg: 'var(--danger-10)', label: 'BLOCKED' },
  flagged: { fg: 'var(--warn)',   bg: 'var(--warn-10)',   label: 'FLAGGED' },
  ok:      { fg: 'var(--ok)',     bg: 'var(--ok-10)',     label: 'OK' },
};

function formatRelative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `Today, ${hh}:${mm}`;
  }
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays >= 1 && diffDays <= 30) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-GB');
}

// ─── Severity dot with glow ───────────────────────────────────────
function SeverityDot({ severity }) {
  const color = SEVERITY[severity]?.fg || 'var(--ts)';
  return (
    <span
      aria-label={SEVERITY[severity]?.label || severity}
      className="inline-block rounded-full flex-shrink-0"
      style={{
        width: 10,
        height: 10,
        background: color,
        boxShadow: `0 0 8px ${color}`,
      }}
    />
  );
}

// ─── Status badge ─────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.ok;
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg }}
    >
      <Circle size={8} fill={s.fg} style={{ color: s.fg }} />
      {s.label}
    </span>
  );
}

// ─── Stat card ────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, tone, testId }) {
  const valueColor = tone === 'ok' ? 'var(--ok)'
                   : tone === 'danger' ? 'var(--danger)'
                   : 'var(--white)';
  return (
    <div data-testid={testId} className="p-5 rounded-lg" style={CARD_STYLE}>
      <div className="flex items-start justify-between mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
          {label}
        </p>
        <div
          className="flex items-center justify-center rounded"
          style={{ width: 28, height: 28, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
        >
          <Icon size={14} style={{ color: 'var(--a)' }} />
        </div>
      </div>
      <p className="font-mono text-3xl font-semibold" style={{ color: valueColor }}>
        {new Intl.NumberFormat('en-GB').format(value)}
      </p>
    </div>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────
function Toggle({ checked, onChange, testId, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
      style={{ background: checked ? 'var(--a)' : 'var(--b2)', opacity: disabled ? 0.5 : 1 }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full transition-transform"
        style={{ left: 2, background: 'var(--white)', transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
      />
    </button>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-2.5" style={{ borderBottom: '1px solid var(--b1)' }}>
      <div className="pr-3 min-w-0">
        <p className="font-body text-sm" style={{ color: 'var(--white)' }}>{label}</p>
        {hint && <p className="font-body text-xs mt-0.5" style={{ color: 'var(--ts)' }}>{hint}</p>}
      </div>
      <Toggle checked={checked} onChange={onChange} testId={testId} />
    </div>
  );
}

// ─── Add IP modal ─────────────────────────────────────────────────
function AddIpModal({ onCancel, onAdd, saving }) {
  const [ip, setIp] = useState('');
  const [label, setLabel] = useState('');
  const [err, setErr] = useState(null);
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onCancel]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await onAdd({ ip_address: ip.trim(), label: label.trim() });
    } catch (ex) {
      setErr(ex.message || 'Add failed');
    }
  };

  return (
    <div
      data-testid="admin-ip-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,5,10,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg p-6"
        style={CARD_STYLE}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
              allowlist · add ip
            </p>
            <h2 className="font-display text-xl font-bold" style={{ color: 'var(--white)' }}>Add IP</h2>
          </div>
          <button type="button" onClick={onCancel} className="p-1" style={{ color: 'var(--ts)' }} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {err && (
          <div className="p-2 rounded mb-3" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
            <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{err}</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>IP Address</label>
            <Input
              data-testid="admin-ip-modal-address"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="e.g. 82.45.12.0/24 or 82.x.x.x"
              className="font-mono text-sm h-10 rounded-[3px]"
              style={INPUT_STYLE}
              required
            />
          </div>
          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Label</label>
            <Input
              data-testid="admin-ip-modal-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Primary office · London"
              className="font-body text-sm h-10 rounded-[3px]"
              style={INPUT_STYLE}
              required
              maxLength={120}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onCancel} className="font-body text-xs px-3 py-2 rounded-[3px]" style={{ color: 'var(--tm)', border: '1px solid var(--b1)' }}>
            Cancel
          </button>
          <Button
            type="submit"
            data-testid="admin-ip-modal-save"
            disabled={saving}
            className="btn-shimmer font-body text-xs px-4 py-2 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Adding…' : 'Add to Allowlist'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Main section ─────────────────────────────────────────────────
export default function SecurityCentreSection() {
  const { adminFetch } = useAdminAuth();
  const [summary, setSummary] = useState({ active_threats: 0, blocked_attempts_today: 0, active_sessions: 0, audit_log_entries: 0 });
  const [events, setEvents] = useState([]);
  const [ips, setIps] = useState([]);
  const [toggles, setToggles] = useState({ enforce_ip_allowlist: true, alert_on_new_ip_login: true, force_2fa_for_admins: true });
  const [auditEntries, setAuditEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [savingIp, setSavingIp] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [sRes, aRes] = await Promise.all([
        adminFetch(`${BACKEND_URL}/api/admin/security`),
        adminFetch(`${BACKEND_URL}/api/admin/security/audit-log?limit=10`),
      ]);
      if (!sRes.ok) throw new Error(`security: ${sRes.status}`);
      if (!aRes.ok) throw new Error(`audit: ${aRes.status}`);
      const s = await sRes.json();
      const a = await aRes.json();
      setSummary(s.summary);
      setEvents(s.security_events);
      setIps(s.ip_allowlist);
      setToggles(s.toggles);
      setAuditEntries(a.entries);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshAuditOnly = async () => {
    try {
      const r = await adminFetch(`${BACKEND_URL}/api/admin/security/audit-log?limit=10`);
      if (r.ok) {
        const body = await r.json();
        setAuditEntries(body.entries);
        setSummary((s) => ({ ...s, audit_log_entries: body.total }));
      }
    } catch { /* best-effort */ }
  };

  const handleAddIp = async ({ ip_address, label }) => {
    setSavingIp(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/security/ip-allowlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip_address, label }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Add failed: ${res.status}`);
      }
      const { ip_row } = await res.json();
      setIps((prev) => [...prev, ip_row]);
      setModalOpen(false);
      toast.success('IP added to allowlist', { description: `${ip_row.ip_address} · ${ip_row.label}` });
      refreshAuditOnly();
    } finally {
      setSavingIp(false);
    }
  };

  const handleRemoveIp = async (row) => {
    setRemovingId(row.id);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/security/ip-allowlist/${row.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Remove failed: ${res.status}`);
      setIps((prev) => prev.filter((r) => r.id !== row.id));
      toast.success('IP removed from allowlist', { description: row.ip_address });
      refreshAuditOnly();
    } catch (err) {
      toast.error('Remove failed', { description: err.message });
    } finally {
      setRemovingId(null);
    }
  };

  const handleToggleChange = async (key, value) => {
    const prev = toggles;
    const next = { ...prev, [key]: value };
    setToggles(next);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/security/toggles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error(`Toggle failed: ${res.status}`);
      const body = await res.json();
      setToggles(body.toggles);
      refreshAuditOnly();
    } catch (err) {
      setToggles(prev);  // revert
      toast.error('Toggle update failed', { description: err.message });
    }
  };

  const handleExportAuditLog = async () => {
    setExporting(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/security/audit-log/export`, { method: 'POST' });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const body = await res.json();
      toast.success('Full audit log exported', { description: `${body.row_count} rows logged for export.` });
      refreshAuditOnly();
    } catch (err) {
      toast.error('Export failed', { description: err.message });
    } finally {
      setExporting(false);
    }
  };

  if (loading && !auditEntries.length) {
    return <div className="p-8 rounded-lg" style={CARD_STYLE}>
      <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading security centre…</p>
    </div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-security-centre-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            section · security-centre
          </p>
          <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>
            Security Centre
          </h2>
        </div>
        <Button
          data-testid="admin-security-export"
          onClick={handleExportAuditLog}
          disabled={exporting}
          className="font-body text-sm h-10 px-4 rounded-[3px]"
          style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)', opacity: exporting ? 0.6 : 1 }}
        >
          <Download size={14} className="mr-1.5" />
          {exporting ? 'Exporting…' : 'Export Audit Log'}
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
          <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}

      {/* 4 stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-security-summary">
        <StatCard label="Active Threats"         value={summary.active_threats}         icon={ShieldAlert} tone="ok"     testId="admin-security-stat-threats" />
        <StatCard label="Blocked Attempts Today" value={summary.blocked_attempts_today} icon={ShieldX}     tone="danger" testId="admin-security-stat-blocked" />
        <StatCard label="Active Sessions"        value={summary.active_sessions}        icon={Users}                    testId="admin-security-stat-sessions" />
        <StatCard label="Audit Log Entries"      value={summary.audit_log_entries}      icon={FileText}                 testId="admin-security-stat-audit" />
      </div>

      {/* Recent Security Events */}
      <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-security-events">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>events</p>
            <h3 className="font-display text-base font-semibold flex items-center gap-2" style={{ color: 'var(--white)' }}>
              <span
                aria-label="live"
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--danger)',
                  boxShadow: '0 0 6px var(--danger)',
                  animation: 'pulseDot 1.5s ease-in-out infinite',
                }}
              />
              Recent Security Events
            </h3>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>
            {events.length} recent
          </span>
        </div>

        <ul>
          {events.map((e, idx) => (
            <li
              key={e.id}
              data-testid={`admin-security-event-${e.id}`}
              className="flex items-start gap-3 py-3"
              style={{ borderBottom: idx === events.length - 1 ? 'none' : '1px solid var(--b1)' }}
            >
              <div className="pt-1.5"><SeverityDot severity={e.severity} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="font-display text-sm font-semibold" style={{ color: 'var(--white)' }}>{e.title}</p>
                  <StatusBadge status={e.status} />
                </div>
                <p className="font-mono text-[11px]" style={{ color: 'var(--ts)' }}>
                  {e.meta.join(' · ')}
                </p>
                <p className="font-mono text-[10px] mt-0.5" style={{ color: 'var(--te)' }}>
                  {formatRelative(e.created_at)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* IP Allowlist Manager */}
      <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-ip-allowlist">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>allowlist</p>
            <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
              IP Allowlist — Admin Panel
            </h3>
          </div>
          <Button
            data-testid="admin-ip-add"
            onClick={() => setModalOpen(true)}
            className="font-body text-xs h-9 px-3 rounded-[3px]"
            style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)' }}
          >
            <Plus size={12} className="mr-1" />
            Add IP
          </Button>
        </div>

        <ul className="mb-5">
          {ips.length === 0 && (
            <li className="py-4 text-center font-body text-sm" style={{ color: 'var(--te)' }}>
              No IPs in allowlist.
            </li>
          )}
          {ips.map((row, idx) => (
            <li
              key={row.id}
              data-testid={`admin-ip-row-${row.id}`}
              className="flex items-center justify-between py-3"
              style={{ borderBottom: idx === ips.length - 1 ? 'none' : '1px solid var(--b1)' }}
            >
              <div>
                <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>{row.ip_address}</p>
                <p className="font-body text-xs mt-0.5" style={{ color: 'var(--ts)' }}>{row.label}</p>
              </div>
              <button
                data-testid={`admin-ip-remove-${row.id}`}
                onClick={() => handleRemoveIp(row)}
                disabled={removingId === row.id}
                title="Remove"
                className="flex items-center justify-center rounded"
                style={{
                  width: 28,
                  height: 28,
                  color: 'var(--danger)',
                  border: '1px solid var(--b1)',
                  opacity: removingId === row.id ? 0.5 : 1,
                }}
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>

        <div data-testid="admin-security-toggles">
          <ToggleRow
            label="Enforce IP Allowlist"
            hint="Block admin logins from IPs not on the allowlist."
            checked={toggles.enforce_ip_allowlist}
            onChange={(v) => handleToggleChange('enforce_ip_allowlist', v)}
            testId="admin-security-toggle-enforce"
          />
          <ToggleRow
            label="Alert on New IP Login"
            hint="Email when an admin signs in from a new IP address."
            checked={toggles.alert_on_new_ip_login}
            onChange={(v) => handleToggleChange('alert_on_new_ip_login', v)}
            testId="admin-security-toggle-alert"
          />
          <ToggleRow
            label="Force 2FA for All Admins"
            hint="Require TOTP verification on every admin login."
            checked={toggles.force_2fa_for_admins}
            onChange={(v) => handleToggleChange('force_2fa_for_admins', v)}
            testId="admin-security-toggle-2fa"
          />
        </div>
      </div>

      {/* Audit Log */}
      <div className="rounded-lg overflow-hidden" style={CARD_STYLE} data-testid="admin-security-audit-log">
        <div className="p-6 flex items-center justify-between" style={{ borderBottom: '1px solid var(--b1)' }}>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>trail · live</p>
            <h3 className="font-display text-base font-semibold flex items-center gap-2" style={{ color: 'var(--white)' }}>
              <ScrollText size={14} style={{ color: 'var(--a)' }} />
              Audit Log — Recent Actions
            </h3>
          </div>
          <Button
            data-testid="admin-security-export-full"
            onClick={handleExportAuditLog}
            disabled={exporting}
            className="font-body text-xs h-9 px-3 rounded-[3px]"
            style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)', opacity: exporting ? 0.6 : 1 }}
          >
            <Download size={12} className="mr-1" />
            Export Full Log
          </Button>
        </div>

        <table className="w-full">
          <thead>
            <tr style={{ background: 'var(--l2)', borderBottom: '1px solid var(--b1)' }}>
              <Th>Admin</Th>
              <Th>Action</Th>
              <Th>When</Th>
              <Th>IP</Th>
            </tr>
          </thead>
          <tbody>
            {auditEntries.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center font-body text-sm" style={{ color: 'var(--te)' }}>
                  No audit entries yet.
                </td>
              </tr>
            )}
            {auditEntries.map((r, idx) => (
              <tr
                key={r.id}
                data-testid={`admin-audit-row-${r.id}`}
                style={{ borderBottom: idx === auditEntries.length - 1 ? 'none' : '1px solid var(--b1)' }}
              >
                <Td><span className="font-mono text-xs" style={{ color: 'var(--a)' }}>{r.admin_email}</span></Td>
                <Td><span className="font-body text-sm" style={{ color: 'var(--tm)' }}>{r.action}</span></Td>
                <Td><span className="font-mono text-xs" style={{ color: 'var(--ts)' }}>{formatRelative(r.created_at)}</span></Td>
                <Td><span className="font-mono text-xs" style={{ color: 'var(--ts)' }}>{r.ip_address}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <AddIpModal onCancel={() => setModalOpen(false)} onAdd={handleAddIp} saving={savingIp} />
      )}
    </div>
  );
}

// ─── Table helpers ─────────────────────────────────────────────────
function Th({ children, align = 'left' }) {
  return (
    <th
      className="font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-3"
      style={{ color: 'var(--ts)', textAlign: align }}
    >
      {children}
    </th>
  );
}
function Td({ children, align = 'left' }) {
  return <td className="px-4 py-3" style={{ textAlign: align, verticalAlign: 'middle' }}>{children}</td>;
}
